import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    EmailAddressInput,
    GOOGLE_WEB_CLIENT_ID,
    GoogleSignInButton,
    OTPInput,
} from '../common/auth/SignInControls';
import brandLogo from '../../assets/images/shopletzy_logo.svg';
import './oauth.css';

import { scopeNames } from './permissions';
import OAuthConsent, { StoreAccessEditor } from './OAuthConsent';
const errorMessage = (code: string, manage = false) =>
    code === 'store_access_changed'
        ? 'Store access changed in another window. Close these details and refresh Connected apps before trying again.'
        : code === 'invalid_login'
        ? 'Sign-in could not be verified. Check your code or use the email linked to your merchant account.'
        : code === 'slow_down'
          ? 'Too many attempts. Please wait and try again.'
          : code === 'access_denied'
            ? manage
                ? 'Your session expired or access is unavailable. Open Connected apps again from your stores and sign in.'
                : 'This request expired or is not allowed. Start again from your AI client.'
            : code === 'invalid_request' ||
                code === 'invalid_target' ||
                code === 'invalid_scope'
              ? manage
                  ? 'Unable to load Connected apps. Open the page again from your stores.'
                  : 'This connection request is invalid. Start again from your AI client.'
              : 'Unable to complete the request. Please try again.';
async function post(
    path: string,
    body: any,
    portalToken?: string,
    manage = false,
) {
    const response = await fetch(path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            ...(portalToken ? { Authorization: 'Bearer ' + portalToken } : {}),
        },
        body: JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(errorMessage(result.error, manage));
    return result;
}

export default function OAuthPage({
    manage = false,
    portalToken,
}: {
    manage?: boolean;
    portalToken?: string;
}) {
    const [request, setRequest] = useState<any>(null);
    const [identity, setIdentity] = useState<any>(null);
    const [email, setEmail] = useState('');
    const [otp, setOTP] = useState('');
    const [sent, setSent] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [includeFutureStores, setIncludeFutureStores] = useState(false);
    const [editingStoreAccess, setEditingStoreAccess] = useState<string | null>(null);
    const [selected, setSelected] = useState<string[]>([]);
    const [additionalScopes, setAdditionalScopes] = useState<string[]>([]);
    const [connections, setConnections] = useState<any[]>([]);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const working = useRef(false);
    const heading = useRef<HTMLHeadingElement>(null);
    const [generation, setGeneration] = useState(0);
    const [cancelled, setCancelled] = useState(false);
    const [connectionState, setConnectionState] = useState('loading');
    const [connectionError, setConnectionError] = useState('');
    const [notice, setNotice] = useState('');
    const [operation, setOperation] = useState('');
    const [pendingDisconnect, setPendingDisconnect] = useState<string | null>(
        null,
    );
    const [disconnecting, setDisconnecting] = useState<string | null>(null);
    const disconnectConfirmation = useRef<HTMLDivElement>(null);
    const [detailsConnection, setDetailsConnection] = useState<string | null>(
        null,
    );
    const accessDrawer = useRef<HTMLDialogElement>(null);
    useEffect(() => {
        if (
            detailsConnection &&
            accessDrawer.current &&
            !accessDrawer.current.open
        ) {
            accessDrawer.current.showModal?.();
        }
    }, [detailsConnection, connectionState]);
    useEffect(() => {
        if (pendingDisconnect) disconnectConfirmation.current?.focus();
    }, [pendingDisconnect]);
    useEffect(() => {
        if (identity) heading.current?.focus();
    }, [identity]);
    function switchAccount() {
        setRequest(null);
        setIdentity(null);
        setSelected([]);
        setAdditionalScopes([]);
        setSent(false);
        setEmail('');
        setOTP('');
        setCooldown(0);
        setIncludeFutureStores(false);
        setEditingStoreAccess(null);
        setError('');
        setNotice('');
        setConnections([]);
        setConnectionState('loading');
        setConnectionError('');
        setCancelled(false);
        setPendingDisconnect(null);
        setGeneration((value) => value + 1);
    }
    const loadConnections = useCallback(
        async (data: any, active: () => boolean = () => true) => {
            setConnectionState('loading');
            setConnectionError('');
            try {
                const result = await post(
                    '/oauth/requests/' + encodeURIComponent(data.requestId),
                    { csrf: data.csrf, action: 'connections' },
                    undefined,
                    manage,
                );
                if (active()) {
                    setConnections(result.connections);
                    setConnectionState('ready');
                }
            } catch (e) {
                if (active()) {
                    setConnectionError(e.message);
                    setConnectionState('error');
                }
            }
        },
        [manage],
    );
    useEffect(() => {
        let active = true;
        document.title = manage
            ? 'Connected apps | Shopletzy'
            : 'Connect your stores | Shopletzy';
        const parameters = Object.fromEntries(
            new URLSearchParams(window.location.search),
        );
        post(
            '/oauth/requests',
            {
                manage,
                parameters,
                googleClientId: GOOGLE_WEB_CLIENT_ID,
            },
            manage ? portalToken : undefined,
            manage,
        )
            .then(async (data) => {
                if (!active) return;
                setRequest(data);
                setAdditionalScopes(manage ? [] : data.optionalScopes || []);
                if (data.identity && manage) {
                    setIdentity(data.identity);
                    await loadConnections(data, () => active);
                }
            })
            .catch((e) => {
                if (active) setError(e.message);
            });
        return () => {
            active = false;
        };
    }, [manage, portalToken, generation, loadConnections]);
    useEffect(() => {
        if (!cooldown) return;
        const timer = window.setTimeout(() => setCooldown(cooldown - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [cooldown]);
    const call = (action: string, fields: any = {}) =>
        post(
            '/oauth/requests/' + encodeURIComponent(request.requestId),
            {
                csrf: request.csrf,
                action,
                ...fields,
            },
            undefined,
            manage,
        );
    async function run(work: () => Promise<void>, label = 'Working…') {
        if (working.current) return;
        working.current = true;
        setBusy(true);
        setOperation(label);
        setError('');
        try {
            await work();
        } catch (e) {
            setError(e.message);
        } finally {
            working.current = false;
            setBusy(false);
            setOperation('');
        }
    }
    async function login(fields: any) {
        const result = await call('login', fields);
        setIdentity(result);
        if (!manage) {
            setSelected(result.stores.map((store) => store.id));
            setIncludeFutureStores(request.supportsFutureStores === true);
        }
        if (manage) await loadConnections(request);
    }
    async function sendCode() {
        await call('otp', { email });
        setSent(true);
        setOTP('');
        setCooldown(60);
    }
    async function finish(action: string) {
        const result = await call(action, {
            storeIds: selected,
            ...(request.supportsFutureStores ? { includeFutureStores } : {}),
            ...(additionalScopes.length ? { additionalScopes } : {}),
        });
        const url = new URL(result.redirect);
        if (
            url.protocol !== 'https:' &&
            !(
                url.protocol === 'http:' &&
                ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)
            )
        )
            throw new Error(
                'Invalid return address. Start again from your AI client.',
            );
        window.location.assign(url.toString());
    }
    return (
        <main
            className={
                manage
                    ? 'oauth-page oauth-manage'
                    : `oauth-page oauth-authorize${identity ? ' oauth-reviewing' : ''}`
            }
        >
            <div className="oauth-card">
                <div className="oauth-brand">
                    <img src={brandLogo} alt="" />
                    Shopletzy
                </div>
                {manage && (
                    <a className="oauth-back" href="/">
                        ← Back to your stores
                    </a>
                )}
                <h1 ref={heading} tabIndex={-1}>
                    {manage
                        ? 'Connected apps'
                        : request
                          ? `Connect ${request.clientName} to Shopletzy`
                          : 'Connect your stores'}
                </h1>
                {!request && !error && <p role="status">Loading connection…</p>}
                {cancelled ? (
                    <section
                        className="oauth-empty"
                        aria-label="Connection cancelled"
                    >
                        <h2>No access granted</h2>
                        <p>
                            You cancelled before signing in. Close this window
                            and return to your app.
                        </p>
                        <button
                            className="secondary"
                            onClick={() => setCancelled(false)}
                        >
                            Resume connection
                        </button>
                    </section>
                ) : (
                    request && (
                        <>
                            {!manage && (
                                <>
                                    <ol
                                        className="oauth-steps"
                                        aria-label="Connection steps"
                                    >
                                        <li
                                            aria-current={
                                                !identity ? 'step' : undefined
                                            }
                                        >
                                            <span>1</span> Sign in
                                        </li>
                                        <li
                                            aria-current={
                                                identity ? 'step' : undefined
                                            }
                                        >
                                            <span>2</span> Choose access
                                        </li>
                                    </ol>
                                    <aside
                                        className="oauth-client-panel"
                                        aria-label="Requesting app"
                                    >
                                        <div className="oauth-client-identity">
                                            <strong>{request.clientName}</strong>
                                            <p>External app · name not verified by Shopletzy</p>
                                        </div>
                                        <div className="oauth-client-origin">
                                            <span>Return domain</span>
                                            <strong>{request.redirectOrigin}</strong>
                                        </div>
                                    </aside>
                                </>
                            )}
                            {!identity ? (
                                <section aria-label="Sign in">
                                    <p>
                                        Sign in with your existing Shopletzy
                                        merchant account.
                                    </p>
                                    {request.googleClientId && (
                                        <GoogleSignInButton
                                            clientId={request.googleClientId}
                                            nonce={request.googleNonce}
                                            disabled={busy}
                                            onCredential={(credential) =>
                                                run(
                                                    () =>
                                                        login({
                                                            provider: 'google',
                                                            credential,
                                                        }),
                                                    'Verifying…',
                                                )
                                            }
                                        />
                                    )}
                                    {request.googleClientId && (
                                        <div className="oauth-divider">
                                            or use email
                                        </div>
                                    )}
                                    {!sent ? (
                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                run(sendCode, 'Sending code…');
                                            }}
                                        >
                                            <label htmlFor="oauth-email">
                                                Email address
                                            </label>
                                            <EmailAddressInput
                                                id="oauth-email"
                                                value={email}
                                                onValueChange={setEmail}
                                                required
                                                disabled={busy}
                                            />
                                            <button disabled={busy}>
                                                {busy
                                                    ? operation
                                                    : 'Send sign-in code'}
                                            </button>
                                        </form>
                                    ) : (
                                        <form
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                run(
                                                    () =>
                                                        login({
                                                            provider: 'otp',
                                                            otp,
                                                        }),
                                                    'Verifying…',
                                                );
                                            }}
                                        >
                                            <p>Code sent to {email}.</p>
                                            <label htmlFor="oauth-otp">
                                                6-digit code
                                            </label>
                                            <OTPInput
                                                id="oauth-otp"
                                                value={otp}
                                                onValueChange={setOTP}
                                                required
                                                disabled={busy}
                                                autoFocus
                                            />
                                            <div className="oauth-actions">
                                                <button
                                                    disabled={
                                                        busy || otp.length !== 6
                                                    }
                                                >
                                                    {busy
                                                        ? operation
                                                        : 'Verify code'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="secondary"
                                                    disabled={
                                                        busy || cooldown > 0
                                                    }
                                                    onClick={() =>
                                                        run(
                                                            sendCode,
                                                            'Sending code…',
                                                        )
                                                    }
                                                >
                                                    {cooldown
                                                        ? `Resend in ${cooldown}s`
                                                        : 'Send another code'}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="secondary"
                                                    disabled={busy}
                                                    onClick={() => {
                                                        setSent(false);
                                                        setOTP('');
                                                    }}
                                                >
                                                    Change email
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                    {!manage && (
                                        <button
                                            className="oauth-text-button"
                                            disabled={busy}
                                            onClick={() => setCancelled(true)}
                                        >
                                            Cancel connection
                                        </button>
                                    )}
                                </section>
                            ) : (
                                <>
                                    <div className="oauth-account">
                                        <div>
                                            <span className="oauth-eyebrow">
                                                Signed in as
                                            </span>
                                            <strong> {identity.name}</strong>
                                            {(identity.email ||
                                                (sent && email)) && (
                                                <span>
                                                    {identity.email || email}
                                                </span>
                                            )}
                                        </div>
                                        {!manage && (
                                            <button
                                                className="oauth-text-button"
                                                disabled={busy}
                                                onClick={switchAccount}
                                            >
                                                Switch account
                                            </button>
                                        )}
                                    </div>
                                    {manage ? (
                                        <section
                                            className="oauth-connections-layout"
                                            aria-label="Connected apps"
                                        >
                                            <p className="oauth-manage-intro">
                                                Manage the apps you’ve allowed
                                                to access your Shopletzy stores.
                                            </p>
                                            {connectionState === 'ready' && (
                                                <p className="oauth-connection-count">
                                                    {connections.length}{' '}
                                                    connected{' '}
                                                    {connections.length === 1
                                                        ? 'app'
                                                        : 'apps'}
                                                </p>
                                            )}
                                            {connectionState === 'loading' && (
                                                <p role="status">
                                                    Loading connected apps…
                                                </p>
                                            )}
                                            {connectionState === 'error' && (
                                                <div
                                                    className="oauth-error-panel"
                                                    role="alert"
                                                >
                                                    <p>{connectionError}</p>
                                                    <button
                                                        className="secondary"
                                                        disabled={busy}
                                                        onClick={() =>
                                                            run(
                                                                () =>
                                                                    loadConnections(
                                                                        request,
                                                                    ),
                                                                'Retrying…',
                                                            )
                                                        }
                                                    >
                                                        Retry
                                                    </button>
                                                </div>
                                            )}
                                            {connectionState === 'ready' &&
                                                !connections.length && (
                                                    <div className="oauth-empty">
                                                        <h2>
                                                            No apps connected
                                                            yet
                                                        </h2>
                                                        <p>
                                                            Apps will appear
                                                            here after you
                                                            connect them to
                                                            Shopletzy and
                                                            approve access to
                                                            your stores.
                                                        </p>
                                                        <p>
                                                            You can review their
                                                            access or disconnect
                                                            them at any time.
                                                        </p>
                                                    </div>
                                                )}
                                            {connectionState === 'ready' &&
                                                connections.map(
                                                    (connection) => (
                                                        <article
                                                            className="oauth-connection"
                                                            key={connection.id}
                                                        >
                                                            <div className="oauth-connection-header">
                                                                <h2>
                                                                    {
                                                                        connection.client_name
                                                                    }
                                                                </h2>
                                                                <span className="oauth-connection-status">
                                                                    Connected
                                                                </span>
                                                            </div>
                                                            <p className="oauth-connection-summary">
                                                                {
                                                                    (
                                                                        connection.stores ||
                                                                        connection.store_ids ||
                                                                        []
                                                                    ).length
                                                                }{' '}
                                                                authorized{' '}
                                                                {(
                                                                    connection.stores ||
                                                                    connection.store_ids ||
                                                                    []
                                                                ).length === 1
                                                                    ? 'store'
                                                                    : 'stores'}
                                                                <span>
                                                                    Expires{' '}
                                                                    {new Date(
                                                                        connection.expires_at,
                                                                    ).toLocaleDateString(
                                                                        undefined,
                                                                        {
                                                                            day: 'numeric',
                                                                            month: 'short',
                                                                            year: 'numeric',
                                                                        },
                                                                    )}
                                                                </span>
                                                            </p>
                                                            <button
                                                                className="oauth-access-button"
                                                                aria-haspopup="dialog"
                                                                onClick={() =>
                                                                    setDetailsConnection(
                                                                        connection.id,
                                                                    )
                                                                }
                                                            >
                                                                View access
                                                                details
                                                            </button>
                                                            {detailsConnection ===
                                                                connection.id && (
                                                                <dialog
                                                                    className="oauth-access-drawer"
                                                                    ref={
                                                                        accessDrawer
                                                                    }
                                                                    aria-label={`Access details for ${connection.client_name}`}
                                                                    onClose={() => {
                                                                        setDetailsConnection(null);
                                                                        setEditingStoreAccess(null);
                                                                    }}
                                                                    onClick={(
                                                                        event,
                                                                    ) => {
                                                                        if (
                                                                            event.target ===
                                                                            event.currentTarget
                                                                        )
                                                                            accessDrawer.current?.close();
                                                                    }}
                                                                >
                                                                    <div className="oauth-drawer-content">
                                                                        <div className="oauth-drawer-header">
                                                                            <div>
                                                                                <span className="oauth-eyebrow">
                                                                                    Access
                                                                                    details
                                                                                </span>
                                                                                <h2>
                                                                                    {
                                                                                        connection.client_name
                                                                                    }
                                                                                </h2>
                                                                            </div>
                                                                            <button
                                                                                className="secondary"
                                                                                onClick={() =>
                                                                                    accessDrawer.current?.close()
                                                                                }
                                                                            >
                                                                                Close
                                                                            </button>
                                                                        </div>
                                                                        <dl className="oauth-connection-details">
                                                                            <dt>
                                                                                Authorized
                                                                                stores
                                                                            </dt>
                                                                            <dd>
                                                                                <ul className="oauth-connected-stores">
                                                                                    {(
                                                                                        connection.stores ||
                                                                                        connection.store_ids.map(
                                                                                            (
                                                                                                id,
                                                                                            ) => ({
                                                                                                id,
                                                                                            }),
                                                                                        )
                                                                                    ).map(
                                                                                        (
                                                                                            store,
                                                                                        ) => (
                                                                                            <li
                                                                                                key={
                                                                                                    store.id
                                                                                                }
                                                                                            >
                                                                                                <strong>
                                                                                                    {store.name ||
                                                                                                        'Store no longer available'}
                                                                                                </strong>
                                                                                                {store.storeName && (
                                                                                                    <span>
                                                                                                        {
                                                                                                            store.storeName
                                                                                                        }
                                                                                                    </span>
                                                                                                )}
                                                                                            </li>
                                                                                        ),
                                                                                    )}
                                                                                </ul>
                                                                                <p className="oauth-muted">{connection.include_future_stores ? 'These are the currently authorized stores. New eligible stores are included automatically unless excluded.' : 'Access applies only to these stores.'} Current merchant roles still apply.</p>
                                                                            </dd>
                                                                            <dt>Store access</dt>
                                                                            <dd>
                                                                                <p>{connection.include_future_stores ? 'New eligible stores are included automatically. Excluded stores stay excluded.' : 'Only the stores selected for this connection. New stores are not included.'}</p>
                                                                                {connection.availableStores && <button className="secondary" disabled={busy} onClick={() => setEditingStoreAccess(editingStoreAccess === connection.id ? null : connection.id)}>{editingStoreAccess === connection.id ? 'Cancel changes' : 'Change store access'}</button>}
                                                                                {editingStoreAccess === connection.id && <StoreAccessEditor
                                                                                    connection={connection}
                                                                                    supportsFutureStores={request.supportsFutureStores}
                                                                                    busy={busy}
                                                                                    onSave={(storeIds, includeFutureStores) => run(async () => {
                                                                                        await call('update_store_access', { connectionId: connection.id, revision: connection.storeAccessRevision, storeIds, includeFutureStores });
                                                                                        setEditingStoreAccess(null);
                                                                                        await loadConnections(request);
                                                                                        setNotice('Store access updated.');
                                                                                    }, 'Saving store access…')}
                                                                                />}
                                                                            </dd>
                                                                            <dt>
                                                                                Permissions
                                                                            </dt>
                                                                            <dd>
                                                                                <ul>
                                                                                    {connection.scopes.map(
                                                                                        (
                                                                                            scope,
                                                                                        ) => (
                                                                                            <li
                                                                                                key={
                                                                                                    scope
                                                                                                }
                                                                                            >
                                                                                                {scopeNames[
                                                                                                    scope
                                                                                                ] ||
                                                                                                    scope}
                                                                                            </li>
                                                                                        ),
                                                                                    )}
                                                                                </ul>
                                                                                {connection.scopes.includes(
                                                                                    'products:write',
                                                                                ) && (
                                                                                    <p className="oauth-muted">
                                                                                        {connection.scopes.includes('orders:fulfill')
                                                                                            ? 'Product text and SEO edits are separate from the fulfillment permissions listed above. Fulfillment can change order status, stock and COD payment records.'
                                                                                            : connection.scopes.some(scope => ['pricing:write', 'catalogue_pricing:write'].includes(scope))
                                                                                            ? 'Product-detail updates cover text and SEO. Price edits use the separate pricing permissions listed above.'
                                                                                            : connection.scopes.includes('inventory:write')
                                                                                            ? 'Product updates cover text and SEO only. Prices cannot be changed.'
                                                                                            : connection.scopes.includes(
                                                                                                    'orders:accept',
                                                                                                )
                                                                                              ? 'Product text and SEO only. Prices and stock cannot be changed.'
                                                                                              : 'Product text and SEO only. Prices, stock and orders cannot be changed.'}
                                                                                    </p>
                                                                                )}
                                                                                {connection.scopes.includes(
                                                                                    'inventory:write',
                                                                                ) && (
                                                                                    <p className="oauth-muted">
                                                                                        Agents
                                                                                        may
                                                                                        set
                                                                                        or
                                                                                        adjust
                                                                                        available
                                                                                        stock
                                                                                        under
                                                                                        your
                                                                                        instructions.
                                                                                        Reserved
                                                                                        and
                                                                                        committed
                                                                                        units
                                                                                        stay
                                                                                        unchanged.
                                                                                    </p>
                                                                                )}
                                                                            </dd>
                                                                            {connection.created_at && (
                                                                                <>
                                                                                    <dt>
                                                                                        Connected
                                                                                    </dt>
                                                                                    <dd>
                                                                                        {new Date(
                                                                                            connection.created_at,
                                                                                        ).toLocaleDateString(
                                                                                            undefined,
                                                                                            {
                                                                                                day: 'numeric',
                                                                                                month: 'short',
                                                                                                year: 'numeric',
                                                                                            },
                                                                                        )}
                                                                                    </dd>
                                                                                </>
                                                                            )}
                                                                            <dt>
                                                                                Connection
                                                                                expires
                                                                            </dt>
                                                                            <dd>
                                                                                {new Date(
                                                                                    connection.expires_at,
                                                                                ).toLocaleDateString(
                                                                                    undefined,
                                                                                    {
                                                                                        day: 'numeric',
                                                                                        month: 'short',
                                                                                        year: 'numeric',
                                                                                    },
                                                                                )}
                                                                            </dd>
                                                                            {connection.renewal_expires_at && (
                                                                                <>
                                                                                    <dt>
                                                                                        Renewal
                                                                                        deadline
                                                                                    </dt>
                                                                                    <dd>
                                                                                        {new Date(
                                                                                            connection.renewal_expires_at,
                                                                                        ).toLocaleDateString(
                                                                                            undefined,
                                                                                            {
                                                                                                day: 'numeric',
                                                                                                month: 'short',
                                                                                                year: 'numeric',
                                                                                            },
                                                                                        )}
                                                                                        <p className="oauth-muted">
                                                                                            The
                                                                                            app
                                                                                            must
                                                                                            renew
                                                                                            access
                                                                                            by
                                                                                            this
                                                                                            date
                                                                                            to
                                                                                            stay
                                                                                            connected.
                                                                                            Each
                                                                                            renewal
                                                                                            extends
                                                                                            this
                                                                                            deadline
                                                                                            by
                                                                                            up
                                                                                            to
                                                                                            90
                                                                                            days,
                                                                                            within
                                                                                            the
                                                                                            connection
                                                                                            expiry.
                                                                                        </p>
                                                                                    </dd>
                                                                                </>
                                                                            )}
                                                                        </dl>
                                                                    </div>
                                                                </dialog>
                                                            )}
                                                            {pendingDisconnect ===
                                                            connection.id ? (
                                                                <div
                                                                    className="oauth-disconnect-confirm"
                                                                    ref={
                                                                        disconnectConfirmation
                                                                    }
                                                                    tabIndex={
                                                                        -1
                                                                    }
                                                                    role="region"
                                                                    aria-label={`Disconnect ${connection.client_name}`}
                                                                >
                                                                    <h3>
                                                                        Disconnect{' '}
                                                                        {
                                                                            connection.client_name
                                                                        }
                                                                        ?
                                                                    </h3>
                                                                    <p>
                                                                        This
                                                                        stops
                                                                        the
                                                                        app’s
                                                                        access
                                                                        to your
                                                                        stores
                                                                        for
                                                                        future
                                                                        requests.
                                                                        Work
                                                                        already
                                                                        in
                                                                        progress
                                                                        may
                                                                        finish.
                                                                        To
                                                                        reconnect,
                                                                        you’ll
                                                                        need to
                                                                        approve
                                                                        access
                                                                        again.
                                                                    </p>
                                                                    <div className="oauth-actions">
                                                                        <button
                                                                            disabled={
                                                                                busy
                                                                            }
                                                                            onClick={() =>
                                                                                run(
                                                                                    async () => {
                                                                                        setDisconnecting(
                                                                                            connection.id,
                                                                                        );
                                                                                        try {
                                                                                            await call(
                                                                                                'revoke',
                                                                                                {
                                                                                                    connectionId:
                                                                                                        connection.id,
                                                                                                },
                                                                                            );
                                                                                            setConnections(
                                                                                                (
                                                                                                    current,
                                                                                                ) =>
                                                                                                    current.filter(
                                                                                                        (
                                                                                                            item,
                                                                                                        ) =>
                                                                                                            item.id !==
                                                                                                            connection.id,
                                                                                                    ),
                                                                                            );
                                                                                            setPendingDisconnect(
                                                                                                null,
                                                                                            );
                                                                                            setNotice(
                                                                                                `${connection.client_name} disconnected.`,
                                                                                            );
                                                                                        } finally {
                                                                                            setDisconnecting(
                                                                                                null,
                                                                                            );
                                                                                        }
                                                                                    },
                                                                                    'Disconnecting…',
                                                                                )
                                                                            }
                                                                        >
                                                                            {disconnecting ===
                                                                            connection.id
                                                                                ? 'Disconnecting…'
                                                                                : 'Confirm disconnect'}
                                                                        </button>
                                                                        <button
                                                                            className="secondary"
                                                                            disabled={
                                                                                busy
                                                                            }
                                                                            onClick={() =>
                                                                                setPendingDisconnect(
                                                                                    null,
                                                                                )
                                                                            }
                                                                        >
                                                                            Keep
                                                                            connected
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    className="oauth-disconnect-button"
                                                                    disabled={
                                                                        busy
                                                                    }
                                                                    onClick={() => {
                                                                        setError(
                                                                            '',
                                                                        );
                                                                        setNotice(
                                                                            '',
                                                                        );
                                                                        setPendingDisconnect(
                                                                            connection.id,
                                                                        );
                                                                    }}
                                                                >
                                                                    Disconnect
                                                                    app
                                                                </button>
                                                            )}
                                                        </article>
                                                    ),
                                                )}
                                        </section>
                                    ) : (
                                        <OAuthConsent
                                            request={request}
                                            stores={identity.stores}
                                            selected={selected}
                                            onSelected={setSelected}
                                            includeFutureStores={includeFutureStores}
                                            onFutureStores={setIncludeFutureStores}
                                            additionalScopes={additionalScopes}
                                            onScopes={setAdditionalScopes}
                                            busy={busy}
                                            operation={operation}
                                            onApprove={() => run(() => finish('approve'), 'Connecting…')}
                                            onCancel={() => run(() => finish('deny'), 'Cancelling…')}
                                        />
                                    )}
                                </>
                            )}
                        </>
                    )
                )}
                {notice && (
                    <p className="oauth-success" role="status">
                        {notice}
                    </p>
                )}
                {busy && (
                    <p className="oauth-sr-only" role="status">
                        {operation}
                    </p>
                )}
                {error && (
                    <p className="oauth-error" role="alert">
                        {error}
                        {!request && (
                            <button
                                className="secondary"
                                onClick={switchAccount}
                            >
                                Try again
                            </button>
                        )}
                    </p>
                )}
                <footer>
                    {manage ? (
                        'Only keep apps you recognize and trust. Disconnecting an app does not undo changes it has already made.'
                    ) : (
                        <>
                            Only approve clients you recognize. You can revoke
                            access at any time from{' '}
                            <a href="/oauth/connections">
                                Shopletzy Connected apps
                            </a>
                            .
                        </>
                    )}
                </footer>
            </div>
        </main>
    );
}
